import type { Duration } from "./bandwidthServiceAdapters";

/**
 * The bandwidth chart's range, as it travels in a detail URL (CTX-773). The `port`,
 * `connection` and `datacenter` slices still hold the live value — they are not refactored
 * — but they stop being its only home, so a reload or a pasted link restores the range.
 *
 * - preset: `?range=1h&interval=20s` (interval optional; the chart snaps a missing or
 *   disallowed one to the smallest allowed for the range)
 * - custom: `?from=<ISO>&to=<ISO>` (the interval is recommended from the range, as the
 *   chart already does, so it is not carried)
 *
 * These are the chart params `detailRoutes` declares as view state.
 */
export type ChartRange =
  | { kind: "preset"; timeRange: Duration; interval: Duration | null }
  | { kind: "custom"; start: string; end: string };

const RANGE = "range";
const INTERVAL = "interval";
const FROM = "from";
const TO = "to";

const DURATION = /^\d+[smhd]$/;

function asDuration(raw: string | null): Duration | null {
  return raw && DURATION.test(raw) ? (raw as Duration) : null;
}

function asInstant(raw: string | null): string | null {
  if (!raw) return null;
  return Number.isFinite(Date.parse(raw)) ? raw : null;
}

/**
 * The range the URL asks for, or null when it asks for none (or for garbage). Only the
 * shape is checked here; whether a range is allowed for this port type is the chart's call.
 */
export function readChartRange(params: URLSearchParams): ChartRange | null {
  const start = asInstant(params.get(FROM));
  const end = asInstant(params.get(TO));
  if (start && end) {
    return Date.parse(start) < Date.parse(end) ? { kind: "custom", start, end } : null;
  }

  const timeRange = asDuration(params.get(RANGE));
  if (!timeRange) return null;
  return { kind: "preset", timeRange, interval: asDuration(params.get(INTERVAL)) };
}

/**
 * `params` with the chart range replaced by `range`; null clears it. Every other param is
 * left as it was, in place.
 */
export function writeChartRange(
  params: URLSearchParams,
  range: ChartRange | null
): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const name of [RANGE, INTERVAL, FROM, TO]) next.delete(name);
  if (!range) return next;

  if (range.kind === "custom") {
    next.set(FROM, range.start);
    next.set(TO, range.end);
    return next;
  }
  next.set(RANGE, range.timeRange);
  if (range.interval) next.set(INTERVAL, range.interval);
  return next;
}

export function sameChartRange(a: ChartRange | null, b: ChartRange | null): boolean {
  if (!a || !b) return a === b;
  if (a.kind === "custom" && b.kind === "custom") return a.start === b.start && a.end === b.end;
  if (a.kind === "preset" && b.kind === "preset") {
    return a.timeRange === b.timeRange && a.interval === b.interval;
  }
  return false;
}
